import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../../apiFetch';
import './PlayerDashboard.css';
import PlayerVideoProfile from './PlayerVideoProfile';
import Header from './Header';
import VSNcareertracker from './VSNcareertracker';
import VSNtransfers from './VSNtransfers';
import Profile from './Profile';
import Opportunities from './Opportunities';
import Inbox from './Inbox'; // ✅ Import actual Inbox component

const VisibilityLimitedStrip = () => (
  <div
    role="alert"
    style={{
      width: "100%",
      backgroundColor: "#000",
      borderBottom: "3px solid #ffdd00",
      boxShadow: "0 4px 18px rgba(255,221,0,0.25)",
      padding: "10px 20px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <div
      style={{
        color: "#ffdd00",
        fontWeight: 800,
        fontSize: "13px",
        letterSpacing: "0.4px",
        maxWidth: "1200px",
        textAlign: "center",
      }}
    >
      ⚠ <span style={{ textTransform: "uppercase" }}>Visibility Limited</span> —
      Your visibility has been limited because you have successfully secured a real-world opportunity. If this is a mistake, please let us know so we can review it.
    </div>
  </div>
);

const PlayerDashboard = () => {
  const [profileImage, setProfileImage] = useState(null);
  const [userId, setUserId] = useState(null);
  const [playerDOB, setPlayerDOB] = useState(null);
  const [parentEmail, setParentEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedOpportunities, setSavedOpportunities] = useState([]);
  const [username, setUsername] = useState(null);
  const [isUnderageFlag, setIsUnderageFlag] = useState(false);
  const [parentConsentProvided, setParentConsentProvided] = useState(false);

  // ✅ NEW: discoverability banner state
  const [isDiscoverable, setIsDiscoverable] = useState(true);

  const role = 'player';

  useEffect(() => {
    console.log("📌 Player DOB state =", playerDOB);
    console.log("📌 isUnderageFlag state =", isUnderageFlag);
    console.log("📌 parentConsentProvided state =", parentConsentProvided);
  }, [playerDOB, isUnderageFlag, parentConsentProvided]);

  // Uses .env: if REACT_APP_UNDERAGE_BYPASS=true => overlay OFF
  //            if REACT_APP_UNDERAGE_BYPASS=false (or missing) => overlay ON
  const SHOW_UNDERAGE_OVERLAY = process.env.REACT_APP_UNDERAGE_BYPASS !== "true";

  useEffect(() => {
    console.log("ENV UNDERAGE BYPASS =", process.env.REACT_APP_UNDERAGE_BYPASS);
    console.log("SHOW_UNDERAGE_OVERLAY =", process.env.REACT_APP_UNDERAGE_BYPASS !== "true");
  }, []);

  useEffect(() => {
    if (!userId) return;

    apiFetch(`/api/online-status/update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId })
    }).catch(err => console.error('❌ Error updating online status:', err));

    const interval = setInterval(() => {
      apiFetch(`/api/online-status/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      }).catch(err => console.error('❌ Error updating online status:', err));
    }, 60000);

    return () => clearInterval(interval);
  }, [userId]);

  useEffect(() => {
    const storedUserId = localStorage.getItem("playerId");

    if (storedUserId) {
      setUserId(Number(storedUserId));

      localStorage.setItem("userRole", "player");

      apiFetch(`/api/player-details/${storedUserId}`)
        .then(async (res) => {
          if (!res.ok) {
            const text = await res.text().catch(() => "");
            throw new Error(
              `Failed to fetch player details (${res.status}): ${text}`
            );
          }
          return res.json();
        })
        .then((data) => {
          console.log("player-details response:", data);
          console.log("DOB from API:", data?.dob, "isUnderage from API:", data?.isUnderage);
          console.log("parentConsentProvided from API:", data?.parentConsentProvided);

          if (data?.profileImageUrl) setProfileImage(data.profileImageUrl);
          if (data?.dob) setPlayerDOB(data.dob);
          if (data?.username) setUsername(data.username);

          // ✅ trust backend underage flag
          if (typeof data?.isUnderage === "boolean") {
            setIsUnderageFlag(data.isUnderage);
          } else {
            setIsUnderageFlag(false);
          }

          // ✅ trust backend parental consent flag
          if (typeof data?.parentConsentProvided === "boolean") {
            setParentConsentProvided(data.parentConsentProvided);
          } else {
            setParentConsentProvided(false);
          }

          // ✅ NEW: trust backend discoverability flag (default true if missing)
          if (typeof data?.isDiscoverable === "boolean") {
            setIsDiscoverable(data.isDiscoverable);
          } else {
            setIsDiscoverable(true);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch player details:", err);
        });
    }
  }, []);

  const handleParentEmailSubmit = async () => {
    if (!parentEmail) return;
    setIsSubmitting(true);
    try {
      const res = await apiFetch(`/api/player-details/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parentEmail }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Failed to submit parent email (${res.status}): ${text}`);
      }

      const data = await res.json();
      alert(data.message || "Parent email submitted successfully");

      if (data?.player?.parentEmail) {
        setParentEmail(data.player.parentEmail);
      }

      if (typeof data?.player?.parentConsentProvided === "boolean") {
        setParentConsentProvided(data.player.parentConsentProvided);
      }
    } catch (err) {
      console.error('Error submitting parent email:', err);
      alert(err.message || "Failed to submit parent email");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("userId");   // legacy
    localStorage.removeItem("userRole");
    localStorage.removeItem("role");
    localStorage.removeItem("playerId");
    localStorage.removeItem("authUid");
    window.location.href = "/login";
  };

  const handleProfileImageChange = (newImage) => setProfileImage(newImage);

  const handlePostOpportunity = (compiledOpportunity, filterOption, filterValue) => {
    setSavedOpportunities([...savedOpportunities, { compiledOpportunity, filterOption, filterValue }]);
  };

  const shouldShowUnderageOverlay =
    SHOW_UNDERAGE_OVERLAY &&
    isUnderageFlag &&
    !parentConsentProvided;

  if (!userId) return <div>Loading...</div>;

  return (
    <div className="player-dashboard">
      {/* ⛔ Temporarily disabled overlay using the toggle */}
      {shouldShowUnderageOverlay && (
        <div className="underage-overlay">
          <div className="underage-content">
            <h2>You are under the age of 18</h2>
            <p>Please provide your parent's email address so we can send the consent form.</p>
            <input
              type="email"
              placeholder="Parent Email"
              value={parentEmail}
              onChange={(e) => setParentEmail(e.target.value)}
              style={{ padding: '8px', margin: '10px 0', width: '100%' }}
            />
            <button
              onClick={handleParentEmailSubmit}
              disabled={isSubmitting}
              style={{ padding: '10px', background: 'yellow', border: 'none', fontWeight: 'bold' }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Parent Email'}
            </button>
            <p style={{ marginTop: '20px' }}>
              <a href="/Parental_Consent_Form_VSN.pdf" download style={{ color: 'yellow' }}>
                Download Consent Form
              </a>
            </p>
          </div>
        </div>
      )}

      {/* ✅ UPDATED: Thin warning strip above Header when visibility is limited (NO link) */}
      {isDiscoverable === false && <VisibilityLimitedStrip />}

      <Header profileImage={profileImage} onLogout={handleLogout} userId={userId} />

      <main className="main-content">
        <div className="grid-container">
          <div className="left-section">
            <div className="card profile-js">
              <Profile
                onProfileImageChange={handleProfileImageChange}
                userId={userId}
                username={username} // ✅ THIS IS THE MISSING LINK
              />
            </div>

            {/* ✅ Inserted real Inbox component here */}
            <div className="card inbox-section">
              <Inbox userId={userId} />
            </div>
          </div>

          <div className="center-section">
            <div className="card opportunities-card">
              <Opportunities playerId={userId} />
            </div>
            <div className="video-profile-section">
              <div className="card large-card">
                <PlayerVideoProfile playerId={userId} />
              </div>
            </div>
          </div>

          <div className="right-section">
            <div className="card">
              <VSNcareertracker userId={userId} />
            </div>
            <div className="card">
              <VSNtransfers userId={userId} role="Player" />
            </div>
          </div>
        </div>
      </main>

      {/* ✅ FOOTER */}
      <footer className="dashboard-footer">
        <p>
          © {new Date().getFullYear()} Virtual Scouting Network
        </p>

        <div className="dashboard-footer-links">
          <a
            href="/privacy-policy.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>

          <span>·</span>

          <a
            href="/terms-of-service.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Service
          </a>
        </div>
      </footer>
    </div>
  );
};

export default PlayerDashboard;

















